// One-time terms acknowledgment after login (Jill 2026-07-17). Shows the full
// Terms of Use and Privacy Notice with a single "I agree" button the first
// time a user opens the portal (and again only when TERMS_VERSION bumps).
// Existing beta users are caught automatically: no acceptance row, so the
// gate appears on their next visit.
//
// HARD RULE (project-offline-pwa): this must NEVER lock a pilot out on dead
// boat wifi. Every network call runs behind a deadline; if the server can't
// answer, the gate stays closed and asks next time it can. Once a user has
// agreed, that fact is cached on the device (and synced to the server when
// reachable), so the question is never re-asked offline.
import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../ui";
import theme from "../theme";
import TermsContent, { TERMS_VERSION } from "./TermsContent";

const ACCEPT_CACHE = (uid) => `termsAccepted_${uid}`; // {version, accepted_at, synced}
const NET_DEADLINE_MS = 6000;
const withDeadline = (p) =>
    Promise.race([
        p,
        new Promise((_, reject) => setTimeout(() => reject(new Error("network deadline")), NET_DEADLINE_MS)),
    ]);

const readCache = (uid) => {
    try {
        const raw = localStorage.getItem(ACCEPT_CACHE(uid));
        const c = raw ? JSON.parse(raw) : null;
        return c && c.version === TERMS_VERSION ? c : null;
    } catch { return null; }
};
const writeCache = (uid, synced) => {
    try {
        localStorage.setItem(ACCEPT_CACHE(uid), JSON.stringify({
            version: TERMS_VERSION, accepted_at: new Date().toISOString(), synced,
        }));
    } catch { /* private mode — the server row (when reachable) still records it */ }
};

async function insertAcceptance(uid) {
    const { error } = await withDeadline(
        supabase.from("terms_acceptances").insert({ auth_user_id: uid, terms_version: TERMS_VERSION })
    );
    // 23505 = unique violation: this user+version is already recorded — success
    if (error && error.code !== "23505") throw error;
}

export default function TermsGate() {
    const { user } = useAuth();
    const uid = user?.id || null;
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!uid) { setShow(false); return; }
        let alive = true;
        const cached = readCache(uid);
        if (cached) {
            // agreed on this device — if the server row never landed (agreed
            // offline), retry quietly in the background
            if (!cached.synced) {
                insertAcceptance(uid).then(() => alive && writeCache(uid, true)).catch(() => { /* next open retries */ });
            }
            return;
        }
        (async () => {
            try {
                const { data, error } = await withDeadline(
                    supabase.from("terms_acceptances").select("id")
                        .eq("auth_user_id", uid).eq("terms_version", TERMS_VERSION).maybeSingle()
                );
                if (!alive) return;
                if (error) return; // can't tell — fail open, ask next time
                if (data) writeCache(uid, true);
                else setShow(true);
            } catch { /* offline — fail open */ }
        })();
        return () => { alive = false; };
    }, [uid]);

    if (!uid || !show) return null;

    const agree = async () => {
        setSaving(true);
        try {
            await insertAcceptance(uid);
            writeCache(uid, true);
        } catch {
            // couldn't reach the server: the agreement is recorded on this
            // device and syncs on a later open — never hold the pilot here
            writeCache(uid, false);
        }
        setShow(false);
        setSaving(false);
    };

    return (
        <div role="dialog" aria-modal="true" aria-label="Terms of Use and Privacy Notice"
            style={{
                position: "fixed", inset: 0, zIndex: 3000, background: "rgba(15,23,42,0.55)",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
            }}>
            <div style={{
                background: theme.tokens.surface, color: theme.tokens.ink, borderRadius: 12,
                boxShadow: theme.tokens.shadowRaised, width: "min(720px, 100%)",
                maxHeight: "min(85vh, 100%)", display: "flex", flexDirection: "column",
            }}>
                <div style={{ padding: "16px 20px 10px", borderBottom: `1px solid ${theme.tokens.border}` }}>
                    <h2 style={{ margin: 0, fontSize: "1.15rem", color: theme.colors.primary }}>
                        Before you continue
                    </h2>
                    <p style={{ margin: "6px 0 0", fontSize: "0.88rem", color: theme.tokens.textMuted }}>
                        Please read and agree to the portal's Terms of Use and Privacy Notice. This is a
                        one-time step; you can re-read it anytime from your profile menu.
                    </p>
                </div>
                <div style={{ overflowY: "auto", padding: "12px 20px", flex: 1 }}>
                    <TermsContent />
                </div>
                <div style={{ padding: "12px 20px", borderTop: `1px solid ${theme.tokens.border}`, display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={agree} disabled={saving} autoFocus>
                        {saving ? "Saving…" : "I agree"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
